import React from 'react';
import { Button, Colors } from 'Views/Components/Button/Button';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { UserVehicleDto, disableUserVehicle } from 'Services/Api/_HumanWritten/UserVehicleService';
import { getMeasurementLabelFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { formatWeight, parseWeightUnit } from 'Util/Weight';
import { stringNotEmpty } from 'Util/TypeGuards';
import alertToast from 'Util/ToastifyUtils';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import If from 'Views/Components/If/If';
import { openVehicleEditModal } from './VehicleEditModal';

export interface VehicleCardProps {
	vehicle: UserVehicleDto;
	onRemove: () => void;
	onUpdate: () => void;
}

export function VehicleCard({ vehicle, onRemove, onUpdate }: VehicleCardProps) {
	const vehicleMakeModel = vehicle.cargoType
		? `${vehicle.cargoType.cargoMake ?? ''} ${vehicle.cargoType.cargoModel ?? ''}`.trim()
		: 'Unknown vehicle';

	// Prefer the customer's exact weight (rendered in the configured display unit)
	// over the selected weight band label.
	const displayUnit = appConfigStore.weightDisplayUnit;
	const hasExactWeight = vehicle.exactWeight != null && stringNotEmpty(vehicle.exactWeightUnit);
	const weightDisplay = hasExactWeight
		? formatWeight(Number(vehicle.exactWeight), parseWeightUnit(vehicle.exactWeightUnit, displayUnit), displayUnit)
		: vehicle.selectedWeight?.label || getMeasurementLabelFromId(vehicle.selectedWeightId);

	const handleEdit = () => {
		openVehicleEditModal(vehicle, onUpdate).catch(() => {
			// Modal was cancelled, do nothing
		});
	};

	const handleRemove = async () => {
		let confirmed = false;
		try {
			confirmed = await newConfirmModal(
				'Remove vehicle',
				<>
					<p>Are you sure you want to remove this vehicle from your account?</p>
					<p>
						<strong>Note: </strong>Existing bookings will still have this vehicle associated with them.
					</p>
				</>,
			);
		} catch {
			// User cancelled
			return;
		}

		if (!confirmed) {
			return;
		}

		try {
			await disableUserVehicle(vehicle.id);
			alertToast('Vehicle removed successfully', 'success', 'Vehicle removed');
			onRemove();
		} catch (error) {
			alertToast('Failed to remove vehicle', 'error', 'Error');
		}
	};

	return (
		<div className="vehicle-card" data-test-id="vehicle-library-card">
			<div className="vehicle-card__info">
				<p className="vehicle-card__make-model">{vehicleMakeModel}</p>
				<p className="vehicle-card__detail">
					{getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego')}: {vehicle.cargoIdentification}
				</p>
				<p className="vehicle-card__detail">
					Length: {getMeasurementLabelFromId(vehicle.selectedLengthId) || 'Not set'}
				</p>
				<If condition={hasExactWeight || vehicle.selectedWeight !== null}>
					<p className="vehicle-card__detail">
						Weight: {stringNotEmpty(weightDisplay) ? weightDisplay : 'Not set'}
					</p>
				</If>
				<If condition={appConfigStore.vehicleWidthEnabled && vehicle.selectedWidthId !== null}>
					<p className="vehicle-card__detail">
						Width: {store.widths?.find(x => x.id === vehicle.selectedWidthId)?.label || 'Not set'}
					</p>
				</If>
			</div>
			<div className="vehicle-card__actions">
				<Button
					className="btn--text"
					colors={Colors.Secondary}
					onClick={handleEdit}
					buttonProps={{ 'data-test-id': 'vehicle-library-edit-button' } as any}
				>
					Edit
				</Button>
				<Button
					className="btn--text"
					colors={Colors.Red}
					onClick={handleRemove}
					buttonProps={{ 'data-test-id': 'vehicle-library-remove-button' } as any}
				>
					Remove
				</Button>
			</div>
		</div>
	);
}
